import { ASSERT_INITIALIZED_ERROR } from '@ubi/js-utils/utils'
import Keycloak from 'keycloak-js'
import { defineStore } from 'pinia'
import { resolveEndpoint } from '@/lib/services'
import { keycloakHealthCheck } from './keycloak-apis'
import type { AuthState, AuthTokenParsed, UserRole } from './types'

let keycloakInstance: Keycloak | undefined
const getKeycloak = () => {
    if (!keycloakInstance) throw new Error(`Trying to use keycloak before init`)
    return keycloakInstance
}

// The token will be refreshed if it expires within minValidity seconds
const tokenMinValidity = 5

async function healthCheck(realm: string) {
    if (await keycloakHealthCheck(realm)) return

    console.error(new Error('Keycloak server seems unreachable'))
}

export const useAuthStore = defineStore('authStore', () => {
    const state: AuthState = {
        realm: null,
        token: null,
        tokenParsed: null,
    }

    // Access `tokenParsed` AND assert it is not null. To be used only where the user should always be authenticated
    const tokenData = () => {
        if (!state.tokenParsed) throw new Error(ASSERT_INITIALIZED_ERROR)
        return state.tokenParsed
    }

    // Access `realm` AND assert it is not null. To be used only after init
    const realmGetter = () => {
        if (!state.realm) throw new Error(ASSERT_INITIALIZED_ERROR)
        return state.realm
    }

    // Check if the user has at least one of the accepted roles.
    // To be used only where the user should always be authenticated.
    const hasRole = (acceptedRoles: UserRole[]) => {
        if (!state.tokenParsed) return false // UMD compatibility
        return acceptedRoles.some((acceptedRole) =>
            state.tokenParsed!.roles.includes(acceptedRole)
        )
    }

    const updateToken = () => {
        const keycloak = getKeycloak()
        if (keycloak.token && keycloak.tokenParsed) {
            state.token = keycloak.token
            state.tokenParsed = keycloak.tokenParsed as AuthTokenParsed
        }
    }

    const login = () => {
        return getKeycloak().login({ idpHint: 'google' })
    }

    const init = (realm: string) => {
        state.realm = realm
        keycloakInstance = new Keycloak({
            url: resolveEndpoint('KeycloakService') + '/auth',
            realm,
            clientId: 'front_2cloud',
        })

        const timeout = setTimeout(() => healthCheck(realmGetter()), 5000)

        return keycloakInstance
            .init({
                onLoad: 'check-sso',
                silentCheckSsoRedirectUri: window.location.origin + '/sso.html',
            })
            .then((auth: boolean) => {
                if (!auth) return login()
                updateToken()
            })
            .finally(() => {
                clearTimeout(timeout)
            })
    }

    const logout = () => {
        getKeycloak().logout()
    }

    const updateAuth = async () => {
        const keycloak = getKeycloak()
        await keycloak
            .updateToken(tokenMinValidity)
            .then((refreshed) => {
                if (refreshed) {
                    updateToken()
                }
            })
            .catch((err) => {
                if (!keycloak.authenticated) {
                    login()
                } else {
                    throw err
                }
            })
    }

    const getAuthorization = async () => {
        await updateAuth()
        return `Bearer ${state.token}`
    }

    return {
        getAuthorization,
        hasRole,
        init,
        logout,
        tokenData,
    }
})
